import { makeAutoObservable, runInAction } from "mobx";
import { OsaNodeCoding } from "../../coding/models/OsaNodeCoding";
import {
  isOsaWayVisibleThroughFilters,
  isNodeVisibleByAttributes,
  isNodeVisibleByTreatment,
  isLifeCycleStateVisibleThroughFilters,
  isVisibleByScore,
} from "../utils/apply-filters";
import { Score, TreatmentType } from "@keita/lanepatrol-sdk";
import {
  AttributeFilter,
  AttributeSorting,
  emptyHlaAttributeFilter,
  HlaAttributeFilter,
  ScoreFilters,
} from "../../map/models/MapFilters";
import { RootStore } from "../../shared/stores/RootStore";
import { OsaNode, Treatment } from "../../osaway/models/OsaNode";
import { LifeCycleStage } from "../../osaway/models/LifeCycleStage";
import { OsaWay } from "../../osaway/models/OsaWay";
import {
  getDefaultAverageDailyTraffic,
  getDefaultAverageRoadSpeed,
  HlaWay,
} from "../../ra/models/HlaWay";
import { HlaLane, HlaWayAdjacentFeatures } from "../../ra/models/HlaLane";

type osaWayId = string;

export class FilterStore {
  rootStore;

  showCra = true;
  osaWayFilters: osaWayId[] = [];
  lifeCycleStageFilters: LifeCycleStage[] = [];
  craScoreFilters: ScoreFilters = {};
  treatmentFilters: TreatmentType[] = [];
  craAttributeFilters: AttributeFilter[] = [];
  hlaAttributeFilters = emptyHlaAttributeFilter();
  hlaVerifiedFilters: boolean[] = [];

  showHla = true;
  hlaScoreFilters: ScoreFilters = {};

  filteredHlaWays: HlaWay[] = [];
  filteredOsaWays: OsaWay[] = [];
  filteredNodes: OsaNode[] = [];
  filteredDistance = 0;

  attributeSorting = AttributeSorting.Alphabetic;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    this.resetFilters();
    runInAction(() => {
      this.showHla = true;
      this.showCra = true;
      this.attributeSorting = AttributeSorting.Alphabetic;
      this.filteredOsaWays = [];
      this.filteredNodes = [];
      this.filteredHlaWays = [];
      this.filteredDistance = 0;
    });
  }

  resetFilters() {
    runInAction(() => {
      this.craAttributeFilters = [];
      this.osaWayFilters = [];
      this.lifeCycleStageFilters = [];
      this.craScoreFilters = {};
      this.hlaScoreFilters = {};
      this.treatmentFilters = [];
      this.hlaVerifiedFilters = [];
      this.hlaAttributeFilters = emptyHlaAttributeFilter();
    });
    if (this.rootStore.forecastStore.isForecastModeActivated()) {
      this.forceCodedLifeCycleStageFilters(this.lifeCycleStageFilters);
    }

    this.filterOsaWays();
    this.filterHlaWays();
  }

  get originalOrForecastedOsaWays(): OsaWay[] {
    const { forecastStore } = this.rootStore;
    return forecastStore.computedValues?.osaWays ?? this.osaWaysAvailableToUser;
  }

  get originalOrForecastedNodes(): Map<string, OsaNode[]> {
    const { forecastStore } = this.rootStore;
    return (
      forecastStore.computedValues?.osaNodes ?? this.nodesAvailableInCurrentMode
    );
  }

  get originalOrForecastedAllNodeCodingsByWayId(): Map<
    string,
    Map<string, OsaNodeCoding>
  > {
    const { forecastStore, codingStore } = this.rootStore;
    return (
      forecastStore.computedValues?.allNodeCodingsByWayId ??
      codingStore.allNodeCodingsByWayId
    );
  }

  get nodesAvailableInCurrentMode(): Map<string, OsaNode[]> {
    const { osaNodeStore } = this.rootStore;
    const availableNodes = new Map<string, OsaNode[]>();
    for (const way of this.osaWaysAvailableToUser) {
      const nodes = osaNodeStore.nodesByWayId.get(way.id);
      if (nodes) {
        availableNodes.set(way.id, nodes);
      }
    }

    return availableNodes;
  }

  get osaWaysAvailableToUser(): OsaWay[] {
    const { osaWayStore } = this.rootStore;

    return osaWayStore.ways.filter((way) => this.isOsaWayAvailableToUser(way));
  }

  filterHlaWays() {
    const { hlaStore } = this.rootStore;

    runInAction(() => {
      this.filteredHlaWays = hlaStore.ways.filter((way) =>
        this.isHlaWayVisible(way),
      );
    });

    hlaStore.updateMapSource();
    this.calculateFilteredDistance();
  }

  filterOsaWays() {
    runInAction(() => {
      this.filteredOsaWays = this.originalOrForecastedOsaWays.filter((way) =>
        this.isOsaWayVisible(way),
      );
    });
    this.filterNodes();
  }

  filterNodes() {
    const allNodes = this.originalOrForecastedNodes;
    const filteredNodes: OsaNode[] = [];

    this.filteredOsaWays.forEach((way) => {
      const nodes = allNodes.get(way.id) ?? [];
      filteredNodes.push(
        ...nodes.filter(
          (s) =>
            this.isNodeVisibleByScore(s.score) &&
            this.isNodeVisibleByAttributes(way.id, s.id!) &&
            this.isNodeVisibleByTreatment(s.treatments),
        ),
      );
    });

    runInAction(() => {
      this.filteredNodes = filteredNodes;
    });

    this.calculateFilteredDistance();
  }

  calculateFilteredDistance() {
    runInAction(() => {
      this.filteredDistance =
        this.filteredNodes.reduce((acc, node) => acc + node.distanceToNext, 0) +
        this.filteredHlaWays.reduce((acc, hlaWay) => acc + hlaWay.lengthM, 0);
    });
  }

  setAttributeSorting(value: AttributeSorting) {
    runInAction(() => {
      this.attributeSorting = value;
    });
  }

  isHlaLaneVisible(lane: HlaLane) {
    if (!this.showHla) return false;
    const attrFilters = this.hlaAttributeFilters;

    const testAdjacentFeatures = (
      features: HlaWayAdjacentFeatures,
    ): boolean => {
      // Hard segregation
      if (
        attrFilters.hardSegregation.length > 0 &&
        !attrFilters.hardSegregation.includes(features.hardSegregation as any)
      )
        return false;

      // Road lane
      if (
        attrFilters.roadLane.length > 0 &&
        !attrFilters.roadLane.includes(features.roadLane as any)
      )
        return false;

      // Number of lanes
      if (
        attrFilters.numberOfLanes.length > 0 &&
        !attrFilters.numberOfLanes.includes(features.numberOfLanes as any)
      )
        return false;

      // Parking
      if (
        attrFilters.vehicleParking.length > 0 &&
        !attrFilters.vehicleParking.includes(features.vehicleParking as any)
      )
        return false;

      // Sidewalk
      if (
        attrFilters.sidewalk.length > 0 &&
        !attrFilters.sidewalk.includes(features.sidewalk as any)
      )
        return false;

      // Object or level change
      if (
        attrFilters.objectOrLevelChange.length > 0 &&
        !attrFilters.objectOrLevelChange.includes(
          features.objectOrLevelChange as any,
        )
      )
        return false;

      // Severe hazard
      if (
        attrFilters.severeHazard.length > 0 &&
        !attrFilters.severeHazard.includes(features.severeHazard as any)
      )
        return false;

      return true;
    };

    // Score
    if (!isVisibleByScore(lane.score, this.hlaScoreFilters)) {
      return false;
    }

    // Facility type
    if (
      attrFilters.facilityType.length > 0 &&
      !attrFilters.facilityType.includes(lane.facilityType as any)
    )
      return false;

    // Flow direction
    if (
      attrFilters.flowDirection.length > 0 &&
      !attrFilters.flowDirection.includes(lane.flowDirection as any)
    )
      return false;

    // Facility width per direction
    if (
      attrFilters.facilityWidthPerDirection.length > 0 &&
      !attrFilters.facilityWidthPerDirection.includes(
        lane.facilityWidthPerDirection as any,
      )
    )
      return false;

    // Light segregation
    if (
      attrFilters.lightSegregation.length > 0 &&
      !attrFilters.lightSegregation.includes(lane.lightSegregation as any)
    )
      return false;

    // Tram or train rails
    if (
      attrFilters.tramOrTrainRails.length > 0 &&
      !attrFilters.tramOrTrainRails.includes(lane.tramOrTrainRails as any)
    )
      return false;

    // Adjacent features
    if (
      !testAdjacentFeatures(lane.adjacentFeaturesLeft) &&
      !testAdjacentFeatures(lane.adjacentFeaturesRight)
    )
      return false;

    return true;
  }

  isHlaWayVisible(way: HlaWay) {
    if (!this.showHla) return false;
    const { projectStore } = this.rootStore;
    const attrFilters = this.hlaAttributeFilters;

    // Verified
    if (
      this.hlaVerifiedFilters.length > 0 &&
      !this.hlaVerifiedFilters.includes(way.verified)
    )
      return false;

    // Type
    if (attrFilters.type.length > 0 && !attrFilters.type.includes(way.type))
      return false;

    // Heavy vehicle traffic
    if (
      attrFilters.heavyVehicleFlow.length > 0 &&
      !attrFilters.heavyVehicleFlow.includes(way.heavyVehicleFlow as any)
    )
      return false;

    // Average road speed
    const averageRoadSpeed = getDefaultAverageRoadSpeed(
      way.type,
      projectStore.distanceUnit,
    );
    if (
      (attrFilters.averageRoadSpeed.min != null &&
        attrFilters.averageRoadSpeed.min >= averageRoadSpeed) ||
      (attrFilters.averageRoadSpeed.max != null &&
        attrFilters.averageRoadSpeed.max <= averageRoadSpeed)
    )
      return false;

    // Average daily traffic
    const averageDailyTraffic = getDefaultAverageDailyTraffic(way.type);
    if (
      (attrFilters.averageDailyTraffic.min != null &&
        attrFilters.averageDailyTraffic.min >= averageDailyTraffic) ||
      (attrFilters.averageDailyTraffic.max != null &&
        attrFilters.averageDailyTraffic.max <= averageDailyTraffic)
    )
      return false;

    // Street light
    if (attrFilters.lit.length > 0 && !attrFilters.lit.includes(way.lit))
      return false;

    // Lanes
    var nValidLanes = 0;
    if (way.frontFacingLane && this.isHlaLaneVisible(way.frontFacingLane)) {
      nValidLanes++;
    }
    if (way.backFacingLane && this.isHlaLaneVisible(way.backFacingLane)) {
      nValidLanes++;
    }
    if (nValidLanes === 0) return false;

    return true;
  }

  isOsaWayAvailableToUser(way: OsaWay) {
    const { authStore } = this.rootStore;
    const hasCompletedOsaWay = way.lifeCycleStage === LifeCycleStage.Completed;
    return authStore.isAuthenticated || hasCompletedOsaWay;
  }

  isOsaWayVisible(way: OsaWay) {
    return (
      this.osaWaysAvailableToUser &&
      this.showCra &&
      isLifeCycleStateVisibleThroughFilters(way, this.lifeCycleStageFilters) &&
      isOsaWayVisibleThroughFilters(way.id, this.osaWayFilters)
    );
  }

  isNodeVisibleByAttributes(osaWayId: string, nodeId: string) {
    if (!this.craAttributeFilters.length) return true;

    const allNodeCodingsByWayId =
      this.originalOrForecastedAllNodeCodingsByWayId;
    const codingEntry = allNodeCodingsByWayId.get(osaWayId)?.get(nodeId);
    if (!codingEntry) return true;

    return isNodeVisibleByAttributes(
      codingEntry.attributes,
      this.craAttributeFilters,
    );
  }

  isNodeVisibleByScore(score: Score | null) {
    return isVisibleByScore(score, this.craScoreFilters);
  }

  isHlaVisibleByScore(score: Score | null) {
    return isVisibleByScore(score, this.hlaScoreFilters);
  }

  isNodeVisibleByTreatment(nodeTreatments: Treatment[] | null) {
    return isNodeVisibleByTreatment(nodeTreatments, this.treatmentFilters);
  }

  setHlaVerifiedFilters(value: boolean[]) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.hlaVerifiedFilters = value;
    });
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setHlaAttributeFilters(value: HlaAttributeFilter) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.hlaAttributeFilters = value;
    });
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setAttributeFilters(value: AttributeFilter[]) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.craAttributeFilters = value;
    });
    this.filterNodes();
    mapStore.recenterMap();
  }

  setCraScoreFilters(value: ScoreFilters) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.craScoreFilters = value;
    });
    this.filterNodes();
    mapStore.recenterMap();
  }

  setHlaScoreFilters(value: ScoreFilters) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.hlaScoreFilters = value;
    });
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setShowHla(value: boolean) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.showHla = value;
    });
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setShowCra(value: boolean) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.showCra = value;
    });
    this.filterOsaWays();
    mapStore.recenterMap();
  }

  setOsaWayFilters(value: string[]) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.osaWayFilters = value;
    });
    this.filterOsaWays();
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setLifeCycleStageFilters(value: LifeCycleStage[]) {
    const { mapStore } = this.rootStore;
    if (this.rootStore.forecastStore.isForecastModeActivated()) {
      this.forceCodedLifeCycleStageFilters(value);
    } else {
      runInAction(() => {
        this.lifeCycleStageFilters = value;
      });
      this.filterOsaWays();
      this.filterHlaWays();
      mapStore.recenterMap();
    }
  }

  forceCodedLifeCycleStageFilters(value: LifeCycleStage[]) {
    runInAction(() => {
      // force selection of Review or Completed
      const codedStages = new Set(value);
      codedStages.delete(LifeCycleStage.Design);
      codedStages.delete(LifeCycleStage.DataCollection);
      codedStages.delete(LifeCycleStage.Coding);
      if (codedStages.size === 0) {
        codedStages.add(LifeCycleStage.Review);
        codedStages.add(LifeCycleStage.Completed);
      }
      this.lifeCycleStageFilters = [...codedStages];
    });
    this.filterOsaWays();
    this.filterHlaWays();
  }

  setTreatmentFilters(value: TreatmentType[]) {
    runInAction(() => {
      this.treatmentFilters = value;
    });
    this.filterNodes();
    this.filterHlaWays();
  }
}
